<template>
  <div class="m-section">
    <div class="m-section__header">
      <div class="row justify-content-between align-items-center">
        <div class="col-md-8 col-12">
          <div class="m-section__headline">Tus auditorías</div>
        </div>
        <div class="col-md-auto col-12" v-if="showsHistoryLink">
          <a class="link-download" @click="goToHistory">Ver todo el historial</a>
        </div>
      </div>
    </div>

    <vue-content-loading
      v-if="isLoading"
      :width="300"
      :height="90"
      primary="#bdc3c7"
      secondary="#95a5a6"
    >
      <svg:rect x="0" y="0" rx="3" ry="3" width="300" height="20" />
      <svg:rect x="0" y="26" rx="3" ry="3" width="300" height="20" />
      <svg:rect x="0" y="52" rx="3" ry="3" width="300" height="20" />
    </vue-content-loading>

    <div v-else-if="isNotConfigured" class="m-card">
      <div class="m-card__header">
        <div class="title-c">Auditorías no disponibles</div>
      </div>
      <div class="m-card__footer">
        <div>
          El módulo de auditorías todavía no está habilitado para tu portal. Cuando lo esté,
          vas a ver acá tu historial y las intervenciones activas.
        </div>
      </div>
    </div>

    <div v-else-if="isUnavailable" class="m-card">
      <div class="m-card__header">
        <div class="title-c">No pudimos cargar tus auditorías</div>
      </div>
      <div class="m-card__main">
        <button class="state__action" :disabled="isRetrying" @click="retry">
          {{ isRetrying ? 'Reintentando…' : 'Reintentar' }}
        </button>
      </div>
      <div class="m-card__footer orange">
        <div>Fue un problema temporal al consultar el sistema de auditorías.</div>
      </div>
    </div>

    <div v-else-if="isEmpty" class="m-card">
      <div class="m-card__header">
        <div class="title-c">Sin auditorías todavía</div>
      </div>
      <div class="m-card__footer">
        <div>Cuando te realicen una auditoría, la vas a ver acá con su puntaje y su escala.</div>
      </div>
    </div>

    <template v-else>
      <div class="row scale-summary">
        <div class="col-md col-6">
          <div class="m-card">
            <div class="m-card__header">
              <div class="title-c">Total auditorías</div>
            </div>
            <div class="m-card__main">
              <div class="letter">{{ scaleSummary.total }}</div>
            </div>
          </div>
        </div>
        <div class="col-md col-6" v-for="scale in scales" :key="scale">
          <div class="m-card">
            <div class="m-card__header">
              <div class="title-c">{{ scale }}</div>
            </div>
            <div class="m-card__main">
              <div class="letter" :style="scaleTextStyle(scale)">{{ countFor(scale) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="hiddenInterventionsCount > 0" class="scheduled">
        {{ hiddenInterventionsCount }}
        {{ hiddenInterventionsCount === 1 ? 'intervención activa' : 'intervenciones activas' }}
        de auditorías fuera de esta lista ·
        <a class="link-download" @click="goToHistory">ver historial completo</a>
      </div>

      <div v-if="scheduledAudits.length > 0" class="scheduled">
        {{ scheduledAudits.length }}
        {{ scheduledAudits.length === 1 ? 'auditoría programada' : 'auditorías programadas' }}
        (aún sin realizar)
      </div>

      <div class="row audits-by-type">
        <div class="col-md-6 col-12 audits-by-type__column">
          <div class="audits-by-type__title">Franquicia</div>
          <div v-if="franchiseAudits.length === 0" class="scheduled">
            Sin auditorías de franquicia en este historial.
          </div>
          <div v-else class="audits-list">
            <auditoria-list-item
              v-for="audit in franchiseAudits"
              :key="audit.id"
              :auditoria="audit"
              @click.native="goToDetail(audit.id)"
            />
          </div>
        </div>

        <div class="col-md-6 col-12 audits-by-type__column">
          <div class="audits-by-type__title">Daily</div>
          <div v-if="dailyAudits.length === 0" class="scheduled">
            Sin auditorías de Daily en este historial.
          </div>
          <div v-else class="audits-list">
            <auditoria-list-item
              v-for="audit in dailyAudits"
              :key="audit.id"
              :auditoria="audit"
              @click.native="goToDetail(audit.id)"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { VueContentLoading } from 'vue-content-loading'
import { mapGetters, mapActions } from 'vuex'
import AuditoriaListItem from '@/views/Auditorias/components/AuditoriaListItem'
import { estiloEscala } from '@/views/Auditorias/escalas'

const MAX_AUDITS_PER_TYPE = 5
const HISTORY_ROUTE = 'auditorias'
const DETAIL_ROUTE = 'auditorias-detalle'
const FRANCHISE_TYPE = 'franquicias'
const KNOWN_SCALES = ['Excelente', 'Satisfactorio', 'Aceptable', 'Deficiente']

export default {
  name: 'AuditoriasPanel',
  components: { VueContentLoading, AuditoriaListItem },
  data () {
    return {
      isRetrying: false,
      scales: KNOWN_SCALES
    }
  },
  computed: {
    ...mapGetters('auditorias', [
      'isLoading',
      'isNotConfigured',
      'isUnavailable',
      'isEmpty',
      'getRatedAudits',
      'getScheduledAudits',
      'getActiveInterventions',
      'getScaleSummary'
    ]),
    franchiseAudits () {
      return this.getRatedAudits
        .filter(audit => audit.tipo === FRANCHISE_TYPE)
        .slice(0, MAX_AUDITS_PER_TYPE)
    },
    dailyAudits () {
      return this.getRatedAudits
        .filter(audit => audit.tipo !== FRANCHISE_TYPE)
        .slice(0, MAX_AUDITS_PER_TYPE)
    },
    scheduledAudits () {
      return this.getScheduledAudits
    },
    activeInterventions () {
      return this.getActiveInterventions
    },
    scaleSummary () {
      return this.getScaleSummary
    },
    hiddenInterventionsCount () {
      const visibleIds = this.franchiseAudits.concat(this.dailyAudits).map(audit => audit.id)
      return this.activeInterventions.filter(intervention => {
        const auditIds = [
          intervention.auditoria_1_id,
          intervention.auditoria_2_id,
          intervention.auditoria_3_id
        ]
        return !auditIds.some(id => visibleIds.includes(id))
      }).length
    },
    showsHistoryLink () {
      return !this.isLoading && !this.isNotConfigured && !this.isUnavailable && !this.isEmpty
    }
  },
  methods: {
    ...mapActions('auditorias', ['RETRY_LOAD']),

    countFor (scale) {
      return this.scaleSummary.byScale[scale] || 0
    },

    scaleTextStyle (scale) {
      return { color: estiloEscala(scale).color }
    },

    async retry () {
      this.isRetrying = true
      try {
        await this.RETRY_LOAD()
      } finally {
        this.isRetrying = false
      }
    },

    goToHistory () {
      this.$router.push({ name: HISTORY_ROUTE })
    },
    goToDetail (id) {
      this.$router.push({ name: DETAIL_ROUTE, params: { id: id } })
    }
  }
}
</script>

<style lang="scss" scoped>
.scale-summary {
  margin-bottom: 14px;
}

.audits-by-type {
  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #32325d;
    margin-bottom: 10px;
  }

  &__column {
    margin-bottom: 14px;
  }
}

.audits-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.state__action {
  border: 0;
  border-radius: 8px;
  background: #27884d;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.scheduled {
  font-size: 12px;
  color: #8898aa;
  padding: 8px 0 12px;
}
</style>
